import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, TrendingUp, Sparkles } from "lucide-react";
import axios from "axios";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorthPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/worth/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-[#64748B]">Loading…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-bold text-[#0F172A]">Nothing here</h1>
          <p className="text-[#64748B] text-sm mt-2">This share link is gone or never existed.</p>
          <Link to="/" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold">
            Try rsell ai →
          </Link>
        </div>
      </div>
    );
  }

  const rec = data.recommended_price || 0;
  const quick = data.prices?.quick_sale;
  const high = data.prices?.high_end;

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0F172A]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/" className="text-[#64748B] hover:text-[#0F172A] text-sm inline-flex items-center gap-1.5 mb-8">
          <span className="font-display font-bold text-[#0F172A]">rsell ai</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bento-card p-6 sm:p-8"
          data-testid="worth-card"
        >
          <div className="flex flex-col sm:flex-row gap-6">
            {data.thumb_b64 && (
              <img
                src={`data:image/jpeg;base64,${data.thumb_b64}`}
                alt=""
                className="w-full sm:w-56 h-56 object-cover rounded-2xl bg-[#F1F5F9]"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="badge-best-match mb-3">
                <Sparkles size={12} /> Best Match
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
                {data.item_title || "Item"}
              </h1>
              {data.brand && (
                <div className="text-[#64748B] text-sm mt-1">by {data.brand}</div>
              )}

              <div className="mt-5 flex items-baseline gap-2">
                <div className="font-mono-num font-bold text-4xl sm:text-5xl tracking-tight text-[#0F172A]">
                  ${rec.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div className="text-[#64748B] text-sm">recommended</div>
              </div>
              {quick != null && high != null && (
                <div className="mt-2 inline-flex items-center gap-2 text-[13px] text-[#64748B]">
                  <TrendingUp size={13} className="text-[#10B981]" />
                  ${quick} – ${high} sold range
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-all active:scale-[0.98] hover:scale-[1.02]"
                  data-testid="worth-cta-scan-yours"
                >
                  <Sparkles size={14} /> Scan yours in 10s
                </Link>
                <a
                  href="https://ebay.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-[#E9ECEF] text-sm font-semibold hover:border-[#0F172A] transition-all"
                >
                  <ExternalLink size={14} /> See comps
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-[#E9ECEF] text-[11px] text-[#94A3B8]">
            Estimate, not a guarantee. Powered by rsell ai — AI resale intelligence for flippers.
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
